// Versão: 1.00
// Data..: 2026-09-26
// Combobox de serviço com busca ao digitar. Filtra o catálogo conforme o texto e,
// quando não há correspondência exata, oferece "+ Criar serviço" que abre um popup
// para cadastrar; ao salvar, o serviço é criado via API e auto-selecionado na linha.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { CommercialService } from '@makweb/shared';
import { api } from '../lib/api.js';
import { useToast } from '../context/ToastContext.js';
import { Modal } from './Modal.js';

interface Props {
  services: CommercialService[];
  value?: string; // serviceId selecionado
  // Chamado ao selecionar um serviço existente (ou ao criar um novo).
  onSelect: (service: CommercialService | null) => void;
  // Notifica o pai que o catálogo mudou (novo serviço criado), para recarregar.
  onServiceCreated?: (service: CommercialService) => void;
  placeholder?: string;
}

// Gera um código de serviço aleatório no cliente (mesma convenção do backend).
function randomServiceCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 5; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `SRV-${s}`;
}

export function ServiceCombobox({ services, value, onSelect, onServiceCreated, placeholder }: Props) {
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Modal de criação rápida.
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', salePrice: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Rótulo do serviço selecionado (mostrado quando o campo não está sendo editado).
  const selected = services.find((s) => s.id === value);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? services.filter((s) => `${s.name} ${s.code ?? ''}`.toLowerCase().includes(q))
    : services;
  // Há correspondência exata pelo nome digitado?
  const exact = services.some((s) => s.name.toLowerCase() === q);

  const pick = (s: CommercialService) => {
    onSelect(s);
    setQuery('');
    setOpen(false);
  };

  const openCreate = () => {
    setForm({ code: randomServiceCode(), name: query.trim(), salePrice: '', notes: '' });
    setCreating(true);
    setOpen(false);
  };

  const saveNew = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await api.post<CommercialService>('/services', {
        code: form.code || undefined,
        name: form.name,
        salePrice: form.salePrice ? Number(form.salePrice) : undefined,
        notes: form.notes || undefined,
        active: true,
      });
      toast.success('Serviço criado');
      onServiceCreated?.(created);
      onSelect(created); // auto-seleciona o novo serviço na linha
      setCreating(false);
      setQuery('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar serviço');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        className="input"
        value={open ? query : (selected?.name ?? '')}
        placeholder={placeholder ?? 'Buscar serviço...'}
        onFocusCapture={() => { setOpen(true); setQuery(''); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
      />
      {open && (
        <div
          className="card"
          style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', maxHeight: 260, overflowY: 'auto', padding: 4, zIndex: 50, boxShadow: 'var(--shadow-lg)' }}
        >
          <button type="button" className="w-full text-left" style={optStyle} onClick={() => { onSelect(null); setQuery(''); setOpen(false); }}>
            <span className="text-dim">— avulso / digitar —</span>
          </button>
          {filtered.map((s) => (
            <button key={s.id} type="button" className="w-full text-left" style={optStyle} onClick={() => pick(s)}>
              {s.name} {s.code && <span className="text-dim text-xs">({s.code})</span>}
              {s.salePrice != null && <span className="text-dim text-xs" style={{ float: 'right' }}>{s.salePrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>}
            </button>
          ))}
          {/* Sem correspondência exata: oferece criar o serviço digitado. */}
          {q && !exact && (
            <button type="button" className="w-full text-left" style={{ ...optStyle, color: 'var(--brand)', fontWeight: 600 }} onClick={openCreate}>
              + Criar serviço "{query.trim()}"
            </button>
          )}
          {filtered.length === 0 && !q && <div className="text-dim text-xs" style={{ padding: 8 }}>Nenhum serviço cadastrado.</div>}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Novo serviço"
        maxWidth={480}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => setCreating(false)}>Cancelar</button>
            <button className="btn btn-primary" type="submit" form="quick-service-form" disabled={saving}>{saving ? 'Salvando...' : 'Criar e selecionar'}</button>
          </>
        }
      >
        <form id="quick-service-form" onSubmit={saveNew}>
          <div className="field"><label className="label">Nome do serviço</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus /></div>
          <div className="field-pair">
            <div className="field mb-0"><label className="label">Código</label>
              <div className="flex gap-1">
                <input className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                <button type="button" className="btn btn-ghost btn-sm" title="Gerar código" onClick={() => setForm({ ...form, code: randomServiceCode() })}>🎲</button>
              </div>
            </div>
            <div className="field mb-0"><label className="label">Valor de venda (R$)</label><input className="input" type="number" min="0" step="0.01" value={form.salePrice} onChange={(e) => setForm({ ...form, salePrice: e.target.value })} /></div>
          </div>
          <div className="field mb-0"><label className="label">Observações</label><textarea className="textarea" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </form>
      </Modal>
    </div>
  );
}

const optStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '8px 10px', border: 'none', background: 'transparent',
  cursor: 'pointer', borderRadius: 6, fontSize: 13,
};
